import { useState } from 'react';
import { useSources, useTriggerSource } from '@/api/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { GlobeSpinner } from '@/components/ui/globe-spinner';
import { PageBanner } from '@/components/layout/PageBanner';
import { SortableHeader, type SortDir } from '@/components/ui/sortable-header';
import { TablePager } from '@/components/ui/table-pager';
import { formatNumber, timeAgo } from '@/lib/utils';
import { Play, Loader2, Database } from 'lucide-react';
import { toast } from 'sonner';

const statusVariant = (status: string): 'success' | 'info' | 'warning' | 'destructive' | 'secondary' => {
  const map: Record<string, 'success' | 'info' | 'warning' | 'destructive' | 'secondary'> = {
    active: 'success', failed: 'destructive', pending: 'warning', disabled: 'secondary',
  };
  return map[status] ?? 'secondary';
};

const statusDot = (status: string) => (
  <Badge variant={statusVariant(status)} className="text-xs capitalize">{status}</Badge>
);

const sourceTypeVariant = (type: string) => {
  const map: Record<string, string> = {
    state_bulk: 'indigo',
    county_portal: 'teal',
    county_socrata: 'purple',
    hk_csdi: 'rose',
    uk_land_registry: 'sky',
  };
  return (map[type] ?? 'secondary') as never;
};

const formatVariant = (fmt: string) => {
  const map: Record<string, string> = {
    arcgis_rest: 'orange',
    socrata_api: 'lime',
    shapefile_download: 'cyan',
    csv: 'fuchsia',
    json_api: 'pink',
    wfs_geojson: 'violet',
  };
  return (map[fmt] ?? 'secondary') as never;
};

export default function SourcesPage() {
  const [stateFilter, setStateFilter] = useState('');
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('state_abbr');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
      setPage(1);
    }
  };

  const { data, isLoading } = useSources({
    state: stateFilter || undefined,
    page,
    sort: sortField,
    order: sortDir,
  });
  const trigger = useTriggerSource();

  const handleTrigger = (sourceId: number, name: string) => {
    trigger.mutate(sourceId, {
      onSuccess: () => toast.success(`Download triggered for ${name}`),
      onError: () => toast.error(`Failed to trigger ${name}`),
    });
  };

  return (
    <div className="space-y-6 animate-fade-up p-1">
      <PageBanner>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="banner-chip">
              <h1 className="text-2xl font-bold banner-title">Data Sources</h1>
            </div>
            {data && (
              <div className="banner-chip">
                <Badge variant="outline" className="text-xs">{data.total} total</Badge>
              </div>
            )}
          </div>
        </div>

        {/* Filter Bar */}
        <div className="banner-chip">
          <label className="text-sm font-medium text-muted-foreground">State</label>
          <Input
            placeholder="e.g. CO"
            value={stateFilter}
            onChange={e => { setStateFilter(e.target.value.toUpperCase().slice(0, 2)); setPage(1); }}
            className="w-24 bg-background/90"
          />
        </div>
      </PageBanner>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-700 dark:text-blue-400" />
            <span className="text-blue-700 dark:text-blue-400">Sources</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <GlobeSpinner label="Loading sources..." />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Country</TableHead>
                    <TableHead>
                      <SortableHeader label="State" field="state_abbr" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Name" field="county_name" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Type" field="source_type" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Format" field="format" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Status" field="status" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Parcels" field="parcel_count" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Last Crawled" field="last_crawled_at" currentField={sortField} currentDir={sortDir} onSort={toggleSort} />
                    </TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.sources.map((s, idx) => (
                    <TableRow
                      key={s.id}
                      className={idx % 2 === 0 ? '' : 'bg-table-row-even'}
                      style={{
                        borderLeft: s.status === 'active' ? '3px solid #34d399'
                          : s.status === 'failed' ? '3px solid #f87171'
                          : s.status === 'pending' ? '3px solid #fbbf24'
                          : '3px solid transparent',
                      }}
                    >
                      <TableCell className="font-mono text-xs">{s.country || 'US'}</TableCell>
                      <TableCell className="font-mono">{s.state_abbr?.trim()}</TableCell>
                      <TableCell className="font-medium">{s.county_name}</TableCell>
                      <TableCell>
                        <Badge variant={sourceTypeVariant(s.source_type)} className="font-mono text-xs">
                          {s.source_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={formatVariant(s.format)} className="font-mono text-xs">
                          {s.format}
                        </Badge>
                      </TableCell>
                      <TableCell>{statusDot(s.status)}</TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(s.parcel_count)}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{timeAgo(s.last_crawled_at)}</TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleTrigger(s.id, s.county_name)}
                          disabled={trigger.isPending || s.status === 'disabled'}
                          title="Trigger download"
                          className="hover:bg-primary/10"
                        >
                          {trigger.isPending && trigger.variables === s.id
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <Play className="h-4 w-4" />}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {data?.sources.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                        No sources found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              <TablePager
                page={page}
                onPageChange={setPage}
                total={data?.total}
                pageSize={data?.limit ?? 20}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
